import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';
import { SUPERADMIN, DASHBOARDEDITOR } from '../constants';
import { FaImage } from 'react-icons/fa';
import { FiEdit } from 'react-icons/fi';
import { GiWheat } from 'react-icons/gi';
import { BsFileEarmarkPdfFill } from 'react-icons/bs';
import MessageModel from './MessageModel';
import MetadataEditForm from './MetadataEditForm'
import { Box, Button, Card, Checkbox, TablePagination, TextField } from '@mui/material';
import CircularLoadingPopUp from './CircularLoadingPopUp';
import ExportCsvPopUp from './ExportCsvPopUp';
import AppMessageModel from './AppMessageModel';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getAllScanPdf, getScanFiles, getScanPdf, getUpdatedScanPdf, getUserCSV, getCompanyMetaData, updateScanUserMetadata, downloadCompanyScanZip } from '../utils/api';

const ACCENT_BLUE = '#22638f';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';
const ROW_EVEN = '#f8fbfe';
const ROW_HOVER = '#eef4fb';

const outlineBtnSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '12.5px',
  color: ACCENT_BLUE,
  borderColor: BORDER,
  backgroundColor: '#fff',
  borderRadius: '8px',
  height: 40,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: ROW_HOVER,
    borderColor: ACCENT_BLUE,
  },
};

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  color: TEXT_MUTED,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: `1px solid ${BORDER}`,
  padding: '8px 12px',
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  fontSize: '13px',
  color: TEXT_BODY,
  borderBottom: `1px solid ${BORDER}`,
  padding: '8px 12px',
  whiteSpace: 'nowrap',
};

const dateLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: TEXT_MUTED,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const dateInputStyle = {
  padding: '9px 10px',
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  fontSize: 13,
  height: 22,
  color: TEXT_BODY,
  backgroundColor: '#fff',
};

export default function ExpendedTableView({ dataList, sourceKey }) {
  const navigate = useNavigate();
  const path = window.location.pathname;
  const user_id = path.split("/").pop();
  const company_id = localStorage.getItem("company_id");
  const userRole = localStorage.getItem("role_name");
  const [localDataList, setLocalDataList] = useState([]);
  const [modifiedData, setModifiedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isOpenModel, setIsOpenModel] = useState(false);
  const [modelType, setModelType] = useState('');
  const [pdfLink, setPdfLink] = useState('');
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTermData] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExportCsv, setIsExportCsv] = useState(false)
  const [metadataKeys, setMetadataKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMetadataColumns, setShowMetadataColumns] = useState(false);
  const [message, setMessage] = useState('')
  const [successfulMessage, setSuccessfulMessage] = useState(false);
  const [tooltip_content, setTooltipContent] = useState([])
  const [metadata_columns, setMetadataColumns] = useState([]);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [editMetadataKeys, setEditMetadataKeys] = useState([]);
  const [editMetadataValues, setEditMetadataValues] = useState({});
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [lastCheckedIndex, setLastCheckedIndex] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSubScan, setIsSubScan] = useState(false);
  const [tableColumns, setTableColumns] = useState([
    'Scan ID', 'Sub Scans', 'Crop', 'Variety', 'Machine', 'Machine Instance ID', 'Score / Grade', 'Moisture', 'Scan Date', 'Action'
  ]);

  useEffect(() => {
    setLocalDataList(dataList);
  }, [dataList]);

  useEffect(() => {
    modifiedDataAccordingToTable(localDataList)
  }, [metadataKeys, localDataList]);

  const calculateAverageOverallScore = (history) => {
    let finalCounts = {};
    let totalKernelCount = 0;

    history.forEach(item => {
      const kernelDetails = item?.kernel_details;
      if (kernelDetails) {
        if (kernelDetails.kernel_counts) {
          Object.entries(kernelDetails.kernel_counts).forEach(([key, value]) => {
            finalCounts[key] = (finalCounts[key] || 0) + value;
          });
        }
        if (kernelDetails.total_kernel_count) {
          totalKernelCount += kernelDetails.total_kernel_count;
        }
      }
    });

    const dependencies = history[0]?.kernel_details?.quality_score_dependencies || [];

    let acc = 0;
    dependencies.forEach(key => {
      const count = finalCounts[key] || 0;
      if (totalKernelCount > 0) {
        const percentage = (count / totalKernelCount) * 100;
        if (!isNaN(percentage)) {
          acc += percentage;
        }
      }
    });

    return acc.toFixed(2);
  };

  const customAndCapatializeClass = (class_array) => {
    const output_data = []
    if (class_array && class_array?.length !== 0) {
      for (const item of class_array) {
        const split_data = item.split(/[-_]/)
        const capitalized_data = split_data.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        output_data.push(capitalized_data.join(' '))
      }
      return output_data
    }
  }

  function createData(item, index) {
    const grade = item.latestScan.kernel_details?.overall_score_grading;
    const score = calculateAverageOverallScore(item.list_subScan);
    const scanId = item.latestScan.scan_id;
    const subScanCount = item.list_subScan.length;
    const crop = item.latestScan.crop_details.crop_name;
    const variety = item.latestScan.variety_details.variety_name;
    const machine = `${item.latestScan.machine_details.machine_name} ${item.latestScan.machine_details.machine_code}`;
    const machineInstanceId = item.latestScan.machine_instance_id || "-";
    const date = moment(item.latestScan.scan_date).format('DD MMM, YYYY hh:mm A');
    const finalScore = grade ? 'NA' : score;
    const moistureForScanMetadata = item?.latestScan?.scan_metadata?.moisture_value
    const quality_score_dependencies = customAndCapatializeClass(item?.latestScan?.kernel_details?.quality_score_dependencies)
    const action = {
      scan_id: item.latestScan.scan_id,
      sub_scan_id: item.latestScan.sub_scan_id,
    };
    const has_updated_result = item.latestScan.has_updated_result
    const user_metadata = item?.latestScan?.scan_metadata?.user_metadata
    const row = {
      scanId,
      subScanCount,
      crop,
      variety,
      machine,
      machineInstanceId,
      date,
      finalScore,
      action,
      has_updated_result,
      user_metadata,
      quality_score_dependencies,
      history: item.list_subScan,
      moistureForScanMetadata,
      index,
    };
    metadataKeys.forEach((metadataKey) => {
      if (item.latestScan?.scan_metadata?.user_metadata && item?.latestScan?.scan_metadata?.user_metadata[metadataKey]) {
        row[metadataKey] = item.latestScan.scan_metadata.user_metadata[metadataKey];
      } else {
        row[metadataKey] = '';
      }
    });
    if (sourceKey === 'scan_history') {
      row.userId = `${item.latestScan?.user_id ?? ''}`;
      row.userName = `${item.latestScan?.user_name ?? ''}`.trim();
      row.contactInfo = item.latestScan?.phone_number ?? item.latestScan?.email ?? 'N/A';
    }
    row.open = false;
    return row;
  }

  const showMetadata = () => {
    setLoading(true);
    setStartDate('');
    setEndDate('');

    if (!showMetadataColumns) {
      getCompanyMetaData(company_id)
        .then((companyMetaDataResponse) => {
          const companyMetadata = companyMetaDataResponse?.data?.data;
          let newColumns = extractMetadataValues(companyMetadata).map((item) =>
            item === 'Moisture' ? 'Moisture metadata' : item
          );

          const metadataKeys = extractMetadataKeys(companyMetadata);

          if (newColumns.length > 0) {
            setMetadataColumns(newColumns);
            setTableColumns([...tableColumns, ...newColumns]);
            setMetadataKeys(metadataKeys);
          } else {
            setSuccessfulMessage(true);
            setMessage('There is No Metadata to Show');
          }
        })
        .catch((error) => {
          console.error('Error fetching metadata: ', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      const filteredColumns = tableColumns.filter((col) => !metadata_columns.includes(col));
      setTableColumns(filteredColumns);
      setMetadataKeys([]);
      setLoading(false);
    }

    setShowMetadataColumns(!showMetadataColumns);
  };

  const extractMetadataValues = (metadata) => {
    return (
      metadata
        ?.filter((item) => item.metadata_value)
        .map((item) => item.metadata_value) || []
    );
  };

  const extractMetadataKeys = (metadata) => {
    return (
      metadata
        ?.filter((item) => item.metadata_value)
        .map((item) => item.metadata_key) || []
    );
  };

  const modifiedDataAccordingToTable = (dataList) => {
    const output = [];
    const groupedData = {};

    dataList.forEach((item) => {
      const { scan_id } = item;
      if (!groupedData[scan_id]) groupedData[scan_id] = [];
      groupedData[scan_id].push(item);
    });

    const keys = Object.keys(groupedData);

    keys.forEach((scan_id) => {
      const sortedSubScans = groupedData[scan_id].sort(
        (a, b) => a.sub_scan_id - b.sub_scan_id
      );

      const firstItem = sortedSubScans[0];

      output.push({
        latestScan: firstItem,
        scan_id,
        list_subScan: sortedSubScans,
      });
    });

    const rows = output.map((item, index) => createData(item, index));
    setModifiedData(rows);

    const searchTermLower = searchTerm.toLowerCase();
    const filtered_rows = rows.filter((row) => {
      const scanId = row.scanId;
      const matchesExistingFields =
        (scanId && scanId.toLowerCase().includes(searchTermLower)) ||
        (row?.crop && row?.crop.toLowerCase().includes(searchTermLower)) ||
        (row?.machine && row?.machine.toLowerCase().includes(searchTermLower));

      const matchesMetadataKeys =
        metadataKeys &&
        metadataKeys.some(
          (key) => key in row && row[key]?.toLowerCase().includes(searchTermLower)
        );

      return matchesExistingFields || matchesMetadataKeys;
    });

    setFilteredData(filtered_rows);
  };

  useEffect(() => {
    if (startDate && endDate) {
      const newStartDate = moment(startDate, "YYYY-MM-DD").startOf("day");
      const newEndDate = moment(endDate, "YYYY-MM-DD").endOf("day");

      const filteredDataList = localDataList.filter((item) => {
        const scanDate = moment(item.scan_date, "DD MMM, YYYY hh:mm A", true);
        if (!scanDate.isValid()) {
          console.warn("Invalid scan_date:", item.scan_date);
          return false;
        }
        return scanDate.isSameOrAfter(newStartDate) && scanDate.isSameOrBefore(newEndDate);
      });

      modifiedDataAccordingToTable(filteredDataList);
    } else {
      modifiedDataAccordingToTable(localDataList);
    }
  }, [startDate, endDate]);

  useMemo(() => {
    let filteredRows = modifiedData;
    if (page !== 0 && searchTerm) {
      setPage(0)
    }
    if (filteredRows.length > 0 && searchTerm) {
      filteredRows = filteredRows.filter((row) => {
        const scanId = row.scanId
        const searchTermLower = searchTerm.toLowerCase();
        const matchesExistingFields =
          (scanId && scanId.toLowerCase().includes(searchTermLower)) ||
          (row?.crop && row?.crop.toLowerCase().includes(searchTermLower)) ||
          (row?.machine && row?.machine.toLowerCase().includes(searchTermLower));
        const matchesMetadataKeys = metadataKeys && metadataKeys?.some((key) => {
          return key in row && row[key]?.toLowerCase().includes(searchTermLower);
        });
        return matchesExistingFields || matchesMetadataKeys;
      });
    }
    setFilteredData(filteredRows);
  }, [searchTerm, metadataKeys]);

  const actionOnImage = async (scan_id, sub_scan_id) => {
    try {
      setIsOpenModel(true);
      setModelType('image');
      const res = await getScanFiles(scan_id, sub_scan_id);
      const scansFilesList = res?.data?.data?.map((files) => {
        const parts = files.file_url.split("/");
        const subScanId = parts[parts.length - 2];

        return {
          image: files.file_url,
          file_name: `${subScanId}_${files.file_name}`,
        };
      });
      setPdfLink(scansFilesList);
    } catch (error) {
      console.error(error);
    }
  };

  const actionOnPdf = async (action) => {
    try {
      setLoadingPDF(true);
      const res = await getScanPdf(action.scan_id);
      const downloadUrl = URL.createObjectURL(res.data);
      window.open(downloadUrl);
    } catch (error) {
      console.error(error);
      setSuccessfulMessage(true);
      setMessage('Unable to download pdf. Something went wrong');
    } finally {
      setLoadingPDF(false);
    }
  };

  const actionOnUpdatedPdf = async (action) => {
    try {
      setLoadingPDF(true);
      const res = await getUpdatedScanPdf(action.scan_id, isSubScan ? action.sub_scan_id : null);
      const downloadUrl = URL.createObjectURL(res.data);
      window.open(downloadUrl);
    } catch (error) {
      console.error(error);
      setSuccessfulMessage(true);
      setMessage('Unable to download pdf. Something went wrong');
    } finally {
      setLoadingPDF(false);
    }
  };

  const actionOnPdfInSubScans = async (action) => {
    try {
      setLoadingPDF(true)
      const res = await getScanPdf(action.scan_id, action.sub_scan_id);
      const downloadUrl = URL.createObjectURL(res.data);
      window.open(downloadUrl)
    } catch (error) {
      console.error(error);
      setSuccessfulMessage(true);
      setMessage('Unable to download pdf. Something went wrong');
    } finally {
      setLoadingPDF(false)
    }
  };

  const onEditClick = async (scanId, metadata) => {
    setSelectedScanId(scanId);
    const res = await getCompanyMetaData(company_id);
    const companyMetadata = res?.data?.data;
    const keys = extractMetadataKeys(companyMetadata);
    setEditMetadataKeys(keys);
    const userMetadata = metadata || {};
    setEditMetadataValues(userMetadata);

    setOpenEditForm(true);
  };

  const onKernalEditClick = async (scanId) => {
    navigate(`/users/${user_id}/${scanId}`);
  };

  const onHandleSelectAllRow = (event) => {
    if (event.target.checked) {
      const isScanHistory = sourceKey === "scan_history";
      if (filteredData && filteredData.length > 0) {
        if (isScanHistory && filteredData.length >= 10) {
          alert("You can select a maximum of 10 scans only.")
          return;
        }

        setSelectedRowData(
          filteredData.map((user) => ({
            id: user.index,
            scan_id: user.action.scan_id,
            sub_scan_id: user.action.sub_scan_id,
            crop: user.crop,
            machine: user.machine,
          }))
        );
      }
    } else {
      setSelectedRowData([]);
    }
  };

  const onHandleSelectEachRow = (event, row, visibleIndex) => {
    const isScanHistory = sourceKey === "scan_history";
    const isChecked = !selectedRowData.some(
      (s) => s.scan_id === row.action.scan_id && s.sub_scan_id === row.action.sub_scan_id
    );

    if (event.shiftKey && lastCheckedIndex !== null) {
      const start = Math.min(lastCheckedIndex, visibleIndex);
      const end = Math.max(lastCheckedIndex, visibleIndex);
      const rangeRows = filteredData.slice(start, end + 1);

      if (isScanHistory) {
        const totalAfter = selectedRowData.length + rangeRows.length;
        if (totalAfter > 10) {
          alert("You can select a maximum of 10 scans only.");
          return;
        }
      }

      const newSelections = rangeRows
        .filter(
          (r) => !selectedRowData.some(
            (s) => s.scan_id === r.action.scan_id && s.sub_scan_id === r.action.sub_scan_id
          )
        )
        .map((r) => ({
          id: r.index,
          scan_id: r.action.scan_id,
          sub_scan_id: r.action.sub_scan_id,
          crop: r.crop,
          machine: r.machine,
        }));

      setSelectedRowData((prev) => [...prev, ...newSelections]);

    } else {
      if (isChecked) {
        if (isScanHistory && selectedRowData.length >= 10) {
          alert("You can select a maximum of 10 scans only.");
          return;
        }
        setSelectedRowData((prev) => [
          ...prev,
          {
            id: row.index,
            scan_id: row.action.scan_id,
            sub_scan_id: row.action.sub_scan_id,
            crop: row.crop,
            machine: row.machine,
          },
        ]);
      } else {
        setSelectedRowData((prev) =>
          prev.filter(
            (s) => !(s.scan_id === row.action.scan_id && s.sub_scan_id === row.action.sub_scan_id)
          )
        );
      }
      setLastCheckedIndex(visibleIndex);
    }
  };

  const onDownloadCsv = async () => {
    setLoadingCSV(true);
    try {
      const path = window.location.pathname;
      const firebaseUserId = path.split('/').pop();

      if (selectedRowData.length > 50) {
        alert("Please select 50 items or fewer for CSV download.");
        return;
      }

      const firstCrop = selectedRowData[0]?.crop;
      const firstMachine = selectedRowData[0]?.machine;

      const allSame = selectedRowData.every(
        (item) => item.crop === firstCrop && item.machine === firstMachine
      );

      if (!allSame) {
        alert(`CSV export requires all selected scans to have the same crop and machine.`);
        return;
      }

      const scanlist = selectedRowData.map((item) => {
        const { id, ...rest } = item;
        return rest;
      });
      await getUserCSV(scanlist, firebaseUserId);

    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCSV(false);
    }
  };

  const onDownloadScanPDF = async () => {
    if (!selectedRowData || selectedRowData.length === 0) {
      alert("Please select at least one item.");
      return;
    }

    if (selectedRowData.length > 30) {
      alert("You can download a maximum of 30 PDFs at a time.");
      return;
    }

    setLoadingCSV(true);

    try {
      const scanlist = selectedRowData.map(({ id, ...rest }) => ({
        ...rest,
      }));

      await getAllScanPdf(scanlist);
    } catch (error) {
      console.error(error);
      alert("Something went wrong while downloading PDF.");
    } finally {
      setLoadingCSV(false);
    }
  };

  const onHandleExpandeView = (rowIndex) => {
    setModifiedData((prevData) => {
      const updatedData = [...prevData];
      updatedData[rowIndex].open = !updatedData[rowIndex].open;
      return updatedData;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSaveMetadata = async (updatedValues) => {
    try {
      const payload = {
        scan_id: selectedScanId,
        user_metadata: updatedValues,
      };

      const response = await updateScanUserMetadata(payload);
      if (response && response.data && response.data.message) {
        const updatedDataList = localDataList.map((row) =>
          row.scan_id === selectedScanId
            ? {
              ...row,
              scan_metadata: {
                ...row.scan_metadata,
                user_metadata: updatedValues,
              },
            }
            : row
        );
        setLocalDataList(updatedDataList);
        modifiedDataAccordingToTable(updatedDataList);
        setSuccessfulMessage(true);
        setMessage(response.data.message);
      }
    } catch (err) {
      console.error("Error updating metadata:", err);
      setMessage('Error updating metadata');
    }
  };

  const handleDownload = async () => {
    try {
      const uniqueScanIds = [...new Set(selectedRowData.map(row => row.scan_id))];
      const payload = {
        scans: uniqueScanIds,
      };
      setIsDownloading(true);
      await downloadCompanyScanZip(payload);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const visibleCropList = filteredData && filteredData.slice(startIndex, endIndex);
  const buttonText = showMetadataColumns ? "Hide Metadata" : "Show Metadata";

  return (
    <div style={{ marginTop: 10 }}>
      {isExportCsv && <ExportCsvPopUp
        open={isExportCsv}
        setOpen={setIsExportCsv}
        allScansList={localDataList}
        selectedRowData={selectedRowData}
      />}
      <Card sx={{ p: 2, boxShadow: 'none', borderRadius: '14px', border: `1px solid ${BORDER}` }}>
        <Box sx={{ marginBottom: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <TextField
                label='Search'
                type='text'
                size="small"
                sx={{
                  width: 220,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    height: 40,
                  },
                }}
                onChange={(e) => setSearchTermData(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={dateLabelSx}>Start Date</label>
                <input
                  type='date'
                  style={dateInputStyle}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={dateLabelSx}>End Date</label>
                <input
                  type='date'
                  style={dateInputStyle}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Box>
              {sourceKey !== 'scan_history' && selectedRowData.length > 0 && (
                <>
                  <Box>
                    {loadingCSV ? (
                      <CircularLoadingPopUp />
                    ) : (
                      <Button variant='outlined' sx={outlineBtnSx} onClick={onDownloadCsv}>
                        Export CSV
                      </Button>
                    )}
                  </Box>

                  <Box>
                    <Button
                      variant='outlined'
                      sx={outlineBtnSx}
                      onClick={() => {
                        const firstCrop = selectedRowData[0]?.crop;
                        const firstMachine = selectedRowData[0]?.machine;

                        const allSame = selectedRowData.every(
                          (item) => item.crop === firstCrop && item.machine === firstMachine
                        );

                        if (!allSame) {
                          alert(`CSV export requires all selected scans to have the same crop and machine.`);
                          return;
                        }

                        setIsExportCsv(true);
                      }}
                    >
                      Crop Wise Csv
                    </Button>
                  </Box>
                </>
              )}
              {
                loading || loadingPDF || isDownloading ? <CircularLoadingPopUp /> : ('')
              }
              <AppMessageModel
                open={successfulMessage}
                setOpen={setSuccessfulMessage}
                message={message}
              />
              {sourceKey !== 'scan_history' && selectedRowData.length > 0 && (
                <Box>
                  {loadingCSV ? <CircularLoadingPopUp /> : (
                    <Button variant='outlined' sx={outlineBtnSx} onClick={onDownloadScanPDF}>
                      Download PDF
                    </Button>
                  )}
                </Box>
              )}
              <Button variant='outlined' sx={outlineBtnSx} onClick={showMetadata}>
                {buttonText}
              </Button>
              {sourceKey === 'scan_history' && userRole === SUPERADMIN && selectedRowData.length > 0 &&
                <Button variant='outlined' sx={outlineBtnSx} onClick={handleDownload}>
                  Download Images
                </Button>}
            </div>
          </div>
        </Box>
        {isOpenModel && <MessageModel open={isOpenModel} onClose={setIsOpenModel} type={modelType} pdfLink={pdfLink} setPdfLink={setPdfLink} />}
        <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '10px', border: `1px solid ${BORDER}`, overflowX: 'auto' }}>
          <Table size='small' aria-label='collapsible table' sx={{ minWidth: 1400 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff', height: 40 }}>
                <TableCell sx={{ ...headerCellSx, width: 40 }} />
                <TableCell sx={{ ...headerCellSx, width: 48 }}>
                  <Checkbox
                    size="small"
                    checked={selectedRowData.length > 0 && modifiedData.length === selectedRowData.length}
                    onChange={onHandleSelectAllRow}
                    sx={{ '&.Mui-checked': { color: ACCENT_BLUE } }}
                  />
                </TableCell>
                {tableColumns && tableColumns.map((col, index) => (
                  <TableCell
                    key={index}
                    sx={headerCellSx}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(visibleCropList.length === 0)
                ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 2} sx={{ textAlign: 'center', border: 'none' }}>
                      <p style={{ padding: 20, fontSize: 15, color: TEXT_MUTED }}>No results found.</p>
                    </TableCell>
                  </TableRow>
                )
                : (
                  visibleCropList?.length !== 0 && visibleCropList?.map((row, index) => (
                    <React.Fragment key={index}>
                      <TableRow
                        sx={{
                          backgroundColor: index % 2 === 0 ? ROW_EVEN : '#fff',
                          '&:hover': { backgroundColor: ROW_HOVER },
                        }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <IconButton
                            disabled={row.subScanCount === 1}
                            aria-label='expand row'
                            size='small'
                            onClick={() => onHandleExpandeView(row.index)}
                          >
                            {row.open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Checkbox
                            size="small"
                            checked={selectedRowData.length > 0 && selectedRowData.some((item) => row.index === item.id)}
                            onClick={(e) => onHandleSelectEachRow(e, row, startIndex + index)}
                            sx={{ '&.Mui-checked': { color: ACCENT_BLUE } }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>{row.scanId} </TableCell>
                        <TableCell sx={bodyCellSx}>{row.subScanCount}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.crop}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.variety}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.machine}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.machineInstanceId}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <p
                            data-tooltip-id="tooltip"
                            style={{ cursor: 'pointer', margin: 0 }}
                            onMouseEnter={() => setTooltipContent(row.quality_score_dependencies)}
                          >
                            {row.finalScore}
                          </p>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>{row.moistureForScanMetadata}</TableCell>
                        <TableCell sx={bodyCellSx}>{row.date}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <FaImage
                            style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer' }}
                            onClick={() => actionOnImage(row.scanId)}
                          />
                          <BsFileEarmarkPdfFill
                            style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer', marginLeft: 8 }}
                            onClick={() => {
                              const isAdmin = userRole === DASHBOARDEDITOR || userRole === SUPERADMIN;
                              if (row.has_updated_result) {
                                if (isAdmin) {
                                  setSelectedRow(row);
                                  setIsSubScan(false);
                                  setPdfModalOpen(true);
                                } else {
                                  actionOnUpdatedPdf(row.action);
                                }
                              } else {
                                actionOnPdf(row.action);
                              }
                            }}
                          />
                          {userRole === SUPERADMIN &&
                            <FiEdit
                              data-tooltip-id="action-tooltip"
                              data-tooltip-content="Edit Metadata"
                              style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer', marginLeft: 8 }}
                              onClick={() => onEditClick(row.scanId, row.user_metadata)}
                            />}
                          {(userRole === DASHBOARDEDITOR || userRole === SUPERADMIN) && sourceKey !== 'scan_history' &&
                            <GiWheat
                              data-tooltip-id="action-tooltip"
                              data-tooltip-content="Edit Kernel Details"
                              style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer', marginLeft: 8 }}
                              onClick={() => onKernalEditClick(row.scanId)}
                            />
                          }
                        </TableCell>
                        {metadataKeys && metadataKeys.map((key) => (
                          <TableCell sx={bodyCellSx} key={key}>
                            {key in row && row[key] !== '' ? row[key] : '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={tableColumns.length + 2}>
                          <Collapse in={row.open} timeout='auto' unmountOnExit>
                            <Box sx={{ margin: 1, backgroundColor: ROW_EVEN, borderRadius: '10px', padding: '12px' }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                                Sub Scans
                              </Typography>
                              <Table size='small' aria-label='purchases'>
                                <TableHead>
                                  <TableRow>
                                    {tableColumns && tableColumns.map((col, index) => (
                                      <TableCell key={index} sx={headerCellSx}>{col}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {[...row.history]
                                    .sort((a, b) => a.sub_scan_id - b.sub_scan_id)
                                    .map((historyRow, index) => (
                                      <TableRow key={index} sx={{ backgroundColor: '#fff' }}>
                                        {sourceKey === 'scan_history' && (
                                          <>
                                            <TableCell sx={bodyCellSx}>{row.userName || '-'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{row.contactInfo || '-'}</TableCell>
                                          </>
                                        )}
                                        <TableCell component='th' scope='row' sx={bodyCellSx}>
                                          {historyRow.scan_id}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{historyRow.sub_scan_id}</TableCell>
                                        <TableCell sx={bodyCellSx}>{historyRow.crop_details.crop_name}</TableCell>
                                        <TableCell sx={bodyCellSx}>{historyRow.variety_details.variety_name}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                          {historyRow.machine_details.machine_name} ({historyRow.machine_details.machine_code})
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{historyRow.machine_instance_id}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                          {historyRow.kernel_details.overall_score_grading ? 'NA' : historyRow.kernel_details?.overall_score}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{historyRow?.scan_metadata?.moisture_value}</TableCell>
                                        <TableCell sx={bodyCellSx}>{moment(historyRow.scan_date).format('DD MMM, YYYY hh:mm A')}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                          <FaImage
                                            style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer' }}
                                            onClick={() => actionOnImage(historyRow.scan_id, historyRow.sub_scan_id)}
                                          />
                                          <BsFileEarmarkPdfFill
                                            style={{ fontSize: 18, color: ACCENT_BLUE, cursor: 'pointer', marginLeft: 8 }}
                                            onClick={() => {
                                              const isAdmin = userRole === DASHBOARDEDITOR || userRole === SUPERADMIN;
                                              if (historyRow.has_updated_result) {
                                                if (isAdmin) {
                                                  setSelectedRow({ action: historyRow });
                                                  setIsSubScan(true);
                                                  setPdfModalOpen(true);
                                                } else {
                                                  actionOnUpdatedPdf(historyRow);
                                                }
                                              } else {
                                                actionOnPdfInSubScans(historyRow);
                                              }
                                            }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${BORDER}`,
              '& .MuiTablePagination-toolbar': { fontSize: '12.5px', color: TEXT_MUTED },
            }}
          />
        </TableContainer>
      </Card>
      <ReactTooltip
        id="tooltip"
        place="top"
        content={<p>Count %  {tooltip_content && tooltip_content?.map((content, index) => <span key={index}><span style={{ fontSize: '20px', fontWeight: 'bold' }}></span>{content}, </span>)} kernels</p>}
        style={{ backgroundColor: '#fff', color: TEXT_BODY, border: `1px solid ${BORDER}` }}
      />
      <ReactTooltip
        id="action-tooltip"
        place="top"
        style={{ backgroundColor: ACCENT_BLUE, color: '#fff', padding: '6px 10px', border: `1px solid ${BORDER}`, fontSize: 12, fontWeight: 600 }}
      />
      <MetadataEditForm
        open={openEditForm}
        onClose={() => setOpenEditForm(false)}
        metadataKeys={editMetadataKeys}
        metadataValues={editMetadataValues}
        onSave={handleSaveMetadata}
      />
      <AppMessageModel
        open={pdfModalOpen}
        setOpen={setPdfModalOpen}
        title="Select PDF Version"
        actions={[
          {
            label: 'Original PDF',
            onClick: () => isSubScan
              ? actionOnPdfInSubScans(selectedRow.action)
              : actionOnPdf(selectedRow.action),
          },
          {
            label: 'Updated PDF',
            onClick: () => actionOnUpdatedPdf(selectedRow.action),
          },
        ]}
      />
    </div>
  );
}